export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail || message;
  }
}

let csrfToken: string | null = null;
let onUnauthorizedHandler: (() => void) | null = null;
let onConflictHandler: ((detail: string) => void) | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function getCsrfToken() {
  return csrfToken;
}

export function setApiHandlers(handlers: {
  onUnauthorized?: () => void;
  onConflict?: (detail: string) => void;
}) {
  if (handlers.onUnauthorized) onUnauthorizedHandler = handlers.onUnauthorized;
  if (handlers.onConflict) onConflictHandler = handlers.onConflict;
}

const BASE_URL = '/api/v1';

export async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Add CSRF token for mutating requests
  if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // Set default Content-Type to application/json if body is not FormData
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorDetail = response.statusText;
    try {
      const errJson = await response.json();
      if (errJson && typeof errJson.detail === 'string') {
        errorDetail = errJson.detail;
      } else if (errJson && typeof errJson.message === 'string') {
        errorDetail = errJson.message;
      }
    } catch {
      // not JSON
    }

    if (response.status === 401) {
      if (onUnauthorizedHandler) {
        onUnauthorizedHandler();
      }
    } else if (response.status === 409) {
      if (onConflictHandler) {
        onConflictHandler(errorDetail || '数据状态有冲突，请刷新后重试。');
      }
    }

    throw new ApiError(
      errorDetail || `请求失败 (${response.status})`,
      response.status,
      errorDetail
    );
  }

  // If response is empty or 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  // Check if content-type is json
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.text()) as unknown as T;
}
